import { useEffect, useState } from 'react';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';

/**
 * Custom hook that provides reactive auth state by subscribing to oktaAuth changes.
 * Mirrors Vue plugin's AuthStateMixin subscription model.
 * 
 * This hook subscribes to Okta's authStateManager and updates whenever
 * authentication state changes (login, logout, token refresh, etc.)
 * 
 * @param oktaAuth - OktaAuth instance to subscribe to
 * @returns Current authentication state or null if not yet initialized
 * 
 * @example
 * ```tsx
 * import { oktaAuth } from '@/lib/okta';
 * import { useAuthState } from '@certara/common-react-components/hooks';
 * 
 * function MyComponent() {
 *   const authState = useAuthState(oktaAuth);
 * 
 *   if (!authState) {
 *     return <Loading />;
 *   }
 * 
 *   if (!authState.isAuthenticated) {
 *     return <LoginPrompt />;
 *   }
 * 
 *   return <AuthenticatedContent />;
 * }
 * ```
 */
export function useAuthState(oktaAuth: OktaAuth): AuthState | null {
  const [authState, setAuthState] = useState<AuthState | null>(
    oktaAuth.authStateManager.getAuthState()
  );

  useEffect(() => {
    setAuthState(oktaAuth.authStateManager.getAuthState());

    const handleAuthStateUpdate = (newAuthState: AuthState) => {
      setAuthState(newAuthState);
    };

    // Subscribe returns an unsubscribe function according to Okta docs,
    // but TypeScript types may show it as void in some versions
    const unsubscribe = oktaAuth.authStateManager.subscribe(handleAuthStateUpdate) as unknown as (() => void) | void;

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [oktaAuth]);

  return authState;
}

